// T061: Effect Text Parser
// 解析官方效果文字，識別關鍵字並轉換為 effectIds

import type { EffectKeyword } from '../../types';
import { getLogger } from '../../utils/logger';

const log = getLogger('EffectTextParser');

/**
 * 效果關鍵字對照表
 * 官方文字 -> effectId
 */
const KEYWORD_MAP: Record<string, EffectKeyword> = {
  // 攻擊效果
  '貫穿': 'pierce',
  '遠程攻擊': 'ranged',
  '遠程': 'ranged',
  '破甲': 'armorBreak',
  '滑壘': 'slide',
  '滑行': 'slide',
  '伏擊': 'ambush',
  '偷襲': 'ambush',
  '飛行': 'flying',
  
  // 防禦效果
  '格擋': 'block',
  '阻擋': 'block',
  '掩護': 'cover',
  '通電': 'electrify',
  '嘲諷': 'taunt',
  '隱匿': 'stealth',
  '保護': 'protect',
  
  // 戰鬥效果
  '劇毒': 'poison',
  '毒': 'poison',
  '分解': 'dismantle',
  '拆卸': 'dismantle',
  '冰凍': 'freeze',
  '燃燒': 'burn',
  '暈眩': 'stun',
  
  // 合體效果
  '機械合體': 'mechaMerge',
  '吞噬合體': 'devourMerge',
  '寄生合體': 'parasiteMerge',
  '堆疊合體': 'stackMerge',
  
  // 資源效果
  '賞金': 'reward',
  '獎勵': 'reward',
  '充能': 'charge',
  '吸取': 'drain',
  '填充': 'overload',
  '超載': 'overload',
  
  // 抗性效果
  '魔法抗性': 'magicResist',
  '魔抗': 'magicResist',
  '破壞抗性': 'destroyResist',
  '效果抗性': 'effectResist',
  
  // 控制效果
  '束縛': 'bind',
  '禁錮': 'bind',
  '橫置': 'tap',
  '衰退': 'decay',
  '衰敗': 'decay',
  '沉默': 'silence',
  '消逝': 'vanish',
  
  // 搭乘效果
  '搭乘': 'board',
  '下車': 'disembark',
  '上車': 'embark',
  '運輸': 'transport',
  
  // 靈魂系統
  '靈魂': 'soul',
  '復活': 'revive',
  '纏繞': 'haunt',
  
  // 其他
  '變形': 'transform',
  '光環': 'aura',
};

/**
 * 解析結果
 */
export interface ParseResult {
  /** 識別到的效果 ID */
  effectIds: EffectKeyword[];
  /** 識別到的觸發條件 */
  triggers: string[];
  /** 識別到的數值 */
  values: Record<string, number>;
  /** 是否有立即效果 */
  hasImmediate: boolean;
  /** 是否有使用時機限制 */
  hasTimingRestriction: boolean;
  /** 使用時機 */
  timing?: string;
  /** 數量上限 */
  quantityLimit?: number;
}

/**
 * 解析效果文字
 * 解析失敗時 gracefully fallback，不拋出錯誤
 */
export function parseEffectText(effectText: string): ParseResult {
  const result: ParseResult = {
    effectIds: [],
    triggers: [],
    values: {},
    hasImmediate: false,
    hasTimingRestriction: false,
  };

  if (!effectText) {
    return result;
  }

  try {
    return parseEffectTextInternal(effectText, result);
  } catch (error) {
    log.warn('解析失敗，返回空結果:', error instanceof Error ? error.message : error);
    return result;
  }
}

/**
 * 內部解析邏輯（可能拋出錯誤，由外層捕獲）
 */
function parseEffectTextInternal(effectText: string, result: ParseResult): ParseResult {

  // 1. 檢查立即效果
  if (effectText.includes('立即')) {
    result.hasImmediate = true;
    result.triggers.push('onSummon');
  }

  // 2. 檢查使用時機
  const timingMatch = effectText.match(/\[\s*使用時機\s*[:：]\s*([^\]]+)\]/);
  if (timingMatch) {
    result.hasTimingRestriction = true;
    result.timing = timingMatch[1].trim();
    
    if (result.timing.includes('主要階段')) {
      result.triggers.push('onTacticsPhase');
    } else if (result.timing.includes('召喚階段')) {
      result.triggers.push('onSummonPhase');
    } else if (result.timing.includes('防守階段')) {
      result.triggers.push('onDefensePhase');
    }
  }

  // 3. 檢查數量上限
  const limitMatch = effectText.match(/【\s*數量上限\s*[:：]\s*(\d+)\s*】/);
  if (limitMatch) {
    result.quantityLimit = parseInt(limitMatch[1], 10);
  }

  // 4. 識別關鍵字效果
  for (const [keyword, effectId] of Object.entries(KEYWORD_MAP)) {
    // 使用 < > 包圍的關鍵字優先
    const bracketPattern = new RegExp(`<\\s*${keyword}\\s*>`, 'i');
    if (bracketPattern.test(effectText)) {
      if (!result.effectIds.includes(effectId)) {
        result.effectIds.push(effectId);
      }
      continue;
    }
    
    // 一般文字匹配
    if (effectText.includes(keyword)) {
      if (!result.effectIds.includes(effectId)) {
        result.effectIds.push(effectId);
      }
    }
  }

  // 5. 識別觸發條件
  const triggerPatterns: Record<string, string> = {
    '召喚時': 'onSummon',
    '入場時': 'onSummon',
    '被召喚時': 'onSummon',
    '打出時': 'onSummon',
    '被破壞時': 'onDestroy',
    '死亡時': 'onDestroy',
    '攻擊時': 'onAttack',
    '進攻時': 'onAttack',
    '被攻擊時': 'onDefend',
    '防禦時': 'onDefend',
    '回合開始': 'onTurnStart',
    '回合結束': 'onTurnEnd',
    '戰鬥成功': 'onKill',
  };

  for (const [pattern, trigger] of Object.entries(triggerPatterns)) {
    if (effectText.includes(pattern) && !result.triggers.includes(trigger)) {
      result.triggers.push(trigger);
    }
  }

  // 6. 提取數值
  const atkMatch = effectText.match(/[+＋](\d+)\s*攻擊力/);
  if (atkMatch) {
    result.values.atkBuff = parseInt(atkMatch[1], 10);
  }

  const atkDebuffMatch = effectText.match(/[-－](\d+)\s*攻擊力/);
  if (atkDebuffMatch) {
    result.values.atkDebuff = parseInt(atkDebuffMatch[1], 10);
  }

  const rewardMatch = effectText.match(/獎勵\s*[+＋]\s*(\d+)/);
  if (rewardMatch) {
    result.values.reward = parseInt(rewardMatch[1], 10);
  }

  const summonCountMatch = effectText.match(/召喚\s*(\d+)\s*個/);
  if (summonCountMatch) {
    result.values.summonCount = parseInt(summonCountMatch[1], 10);
  }

  return result;
}

/**
 * 從卡牌資料取得效果（優先使用預解析資料，fallback 到即時解析）
 */
export function getCardEffects(card: { effectText?: string; parsedKeywords?: string[]; parsedEffects?: { triggers?: string[]; actions?: unknown[]; keywords?: string[] } }): ParseResult {
  // Use pre-parsed if available
  if (card.parsedKeywords && card.parsedKeywords.length > 0) {
    const result: ParseResult = {
      effectIds: card.parsedKeywords as EffectKeyword[],
      triggers: card.parsedEffects?.triggers ?? [],
      values: {},
      hasImmediate: card.parsedEffects?.triggers?.includes('immediate') ?? false,
      hasTimingRestriction: false,
    };
    return result;
  }
  // Fallback to runtime parsing
  return parseEffectText(card.effectText ?? '');
}

/**
 * 從效果文字取得 effectIds 列表
 */
export function getEffectIds(effectText: string): EffectKeyword[] {
  return parseEffectText(effectText).effectIds;
}

/**
 * 檢查效果文字是否包含特定效果
 */
export function hasEffect(effectText: string, effectId: EffectKeyword): boolean {
  return getEffectIds(effectText).includes(effectId);
}

/**
 * 取得效果的優先顯示文字
 */
export function getDisplayKeywords(effectText: string): string[] {
  const keywords: string[] = [];
  
  // 提取 < > 包圍的關鍵字
  const bracketMatches = effectText.match(/<\s*([^>]+?)\s*>/g);
  if (bracketMatches) {
    for (const match of bracketMatches) {
      const keyword = match.replace(/<|>/g, '').trim();
      if (!keywords.includes(keyword)) {
        keywords.push(keyword);
      }
    }
  }
  
  return keywords;
}

// 匯出為物件供舊版相容
export const EffectTextParser = {
  parse: parseEffectText,
  getEffectIds,
  hasEffect,
  getDisplayKeywords,
};
